import { Checkbox } from '@eventbrite/marmalade';
import { logEvent } from '@eventbrite/statsig';
import loadable from '@loadable/component';
import queryString from 'query-string';
import React, { useState } from 'react';
import {
    CUSTOM_DATE,
    DATES_MAPPING_SEARCH,
    EVENTBRITE_FORMAT_MAPPING_SEARCH,
    EXPERIENCES_MAPPING_SEARCH,
    PRICE_MAPPING,
} from '../../../../constants';
import { STATSIG_EXPERIENCE_FILTER_ClICK } from '../../../../constants/analytics';
import {
    CURRENCY_FILTER,
    DATE_FILTER,
    EXPERIENCES_FILTER,
    FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP,
    FORMATTED_CURRENCIES,
    FORMATTED_LANGUAGES,
    FORMAT_FILTER,
    LANGUAGE_FILTER,
    PRICE_FILTER,
} from '../../../../constants/filters';
import { useServerEnrolledStatsigExperiment } from '../../../../context/experiments';
import {
    ExperienceFiltersExperiment,
    ExperienceFiltersExperimentValues,
} from '../../../../types';
import { FilterItem, FilterSection } from './FilterSection';
import FollowedOrganizersFilterButton from './FollowedOrganizersFilterButton';
import { NestedCategoryFilters as CategoryFilters } from './NestedFilters';
import OnlineEventsFilterButton from './OnlineEventsFilterButton';
import { FilterProps, RadioType } from './types';
import { constructFilterItemKey } from './utils';

const InlineDatePicker = loadable(() => import('./InlineDatePicker'));

const DateFilters: React.FunctionComponent<FilterProps> = (props) => {
    const urlParams = queryString.parse(props.location.search.replace('?', ''));
    const [currentDateOption, setCurrentDateOption] = useState<string>('');
    const activeFilterOption = props.selectedFilters?.[DATE_FILTER]?.toString();

    const handleOptionChange = (value: string) => {
        if (value !== CUSTOM_DATE) {
            props.onFilterChange?.(DATE_FILTER, value);
            setCurrentDateOption(value);
        } else if (currentDateOption === CUSTOM_DATE) {
            setCurrentDateOption('');
        } else {
            setCurrentDateOption(value);
        }
    };

    const handleCustomDateSelect = (startDate: string, endDate: string) => {
        setCurrentDateOption('');

        props?.onCustomDateChange?.(startDate, endDate);
    };

    return (
        <FilterSection
            filters={{
                ...DATES_MAPPING_SEARCH,
            }}
            onOptionChange={(val: string) => handleOptionChange(val)}
            title={FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[DATE_FILTER].label}
            activeFilterOption={activeFilterOption}
            isMobile={props.isMobile}
            shouldScrollIntoView={props.filterTypeToShow === DATE_FILTER}
        >
            {(filterItem: RadioType) => {
                const shouldRenderDatePicker =
                    currentDateOption === CUSTOM_DATE &&
                    filterItem.value === CUSTOM_DATE;
                return (
                    <FilterItem
                        key={constructFilterItemKey(
                            'date',
                            filterItem.value,
                            props.isMobile,
                        )}
                        value={filterItem.value}
                        display={filterItem.display}
                    >
                        {shouldRenderDatePicker ? (
                            <InlineDatePicker
                                onDatePickerChange={handleCustomDateSelect}
                                urlParams={urlParams}
                            />
                        ) : null}
                    </FilterItem>
                );
            }}
        </FilterSection>
    );
};

const ExperiencesFilters: React.FunctionComponent<FilterProps> = (props) => {
    const shouldExperienceFiltersBeEnabled = props.locale === 'en_US';
    const ExperienceFiltersExperimentVariant =
        useServerEnrolledStatsigExperiment<ExperienceFiltersExperimentValues>({
            name: ExperienceFiltersExperiment.name,
            paramName: ExperienceFiltersExperiment.paramName,
            defaultValue: ExperienceFiltersExperiment.values.control,
            enabled: shouldExperienceFiltersBeEnabled,
        }).variant;

    const _logExperienceFilterUpdateToStatsig = (
        appliedExperienceFilters?: string[],
        selectedExperiences?: string[],
    ) => {
        if (!selectedExperiences || !appliedExperienceFilters) {
            return;
        }
        let selectedExperience = '';
        let isApplyingFilter = '';
        if (selectedExperiences.length > appliedExperienceFilters.length) {
            // the user has selected a new experience filter
            selectedExperience =
                selectedExperiences[selectedExperiences.length - 1];
            isApplyingFilter = 'true';
        } else {
            // the user is removing an experience filter
            selectedExperience = appliedExperienceFilters.filter(
                (experience: string) =>
                    !selectedExperiences.includes(experience),
            )[0];
            isApplyingFilter = 'false';
        }
        logEvent(STATSIG_EXPERIENCE_FILTER_ClICK, undefined, {
            experience: selectedExperience,
            isApplyingFilter,
            location: 'filterPanel',
        });
    };

    if (
        !shouldExperienceFiltersBeEnabled ||
        ExperienceFiltersExperimentVariant === 'hide'
    ) {
        return null;
    }

    return (
        <FilterSection
            filters={{
                ...EXPERIENCES_MAPPING_SEARCH,
            }}
            onOptionChange={(value: string[]) => {
                props.onFilterChange?.(EXPERIENCES_FILTER, value);
                _logExperienceFilterUpdateToStatsig(
                    props.selectedFilters.experiences,
                    value,
                );
            }}
            title={
                FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[EXPERIENCES_FILTER]
                    .label
            }
            activeFilterOption={props.selectedFilters?.[EXPERIENCES_FILTER]}
            shouldScrollIntoView={props.filterTypeToShow === EXPERIENCES_FILTER}
            isMobile={props.isMobile}
        >
            {(filterItem: RadioType) => {
                return (
                    <FilterItem
                        ChoiceComponent={Checkbox}
                        key={constructFilterItemKey(
                            'experiences',
                            filterItem.value,
                            props.isMobile,
                        )}
                        value={filterItem.value}
                        display={filterItem.display}
                    />
                );
            }}
        </FilterSection>
    );
};

const PriceFilters: React.FunctionComponent<FilterProps> = (props) => {
    return (
        <FilterSection
            filters={{
                ...PRICE_MAPPING,
            }}
            onOptionChange={(value: any) => {
                props.onFilterChange?.(PRICE_FILTER, value);
            }}
            title={FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[PRICE_FILTER].label}
            activeFilterOption={props.selectedFilters?.[PRICE_FILTER]}
            isMobile={props.isMobile}
            shouldScrollIntoView={props.filterTypeToShow === PRICE_FILTER}
        >
            {(filterItem: RadioType) => {
                return (
                    <FilterItem
                        key={constructFilterItemKey(
                            'price',
                            filterItem.value,
                            props.isMobile,
                        )}
                        value={filterItem.value}
                        display={filterItem.display}
                    />
                );
            }}
        </FilterSection>
    );
};

const FormatFilters: React.FunctionComponent<FilterProps> = (props) => {
    return (
        <FilterSection
            filters={{
                ...EVENTBRITE_FORMAT_MAPPING_SEARCH,
            }}
            onOptionChange={(value: any) => {
                props.onFilterChange?.(FORMAT_FILTER, value);
            }}
            title={FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[FORMAT_FILTER].label}
            activeFilterOption={props.selectedFilters?.[FORMAT_FILTER]}
            shouldScrollIntoView={props.filterTypeToShow === FORMAT_FILTER}
            isMobile={props.isMobile}
        >
            {(filterItem: RadioType) => {
                return (
                    <FilterItem
                        key={constructFilterItemKey(
                            'format',
                            filterItem.value,
                            props.isMobile,
                        )}
                        value={filterItem.value}
                        display={filterItem.display}
                    />
                );
            }}
        </FilterSection>
    );
};

const LanguageFilters: React.FunctionComponent<FilterProps> = (props) => {
    return (
        <FilterSection
            filters={{
                ...FORMATTED_LANGUAGES,
            }}
            onOptionChange={(value: any) => {
                props.onFilterChange?.(LANGUAGE_FILTER, value);
            }}
            title={
                FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[LANGUAGE_FILTER].label
            }
            activeFilterOption={props.selectedFilters?.[LANGUAGE_FILTER]}
            shouldScrollIntoView={props.filterTypeToShow === LANGUAGE_FILTER}
            defaultActiveFilterOption={[]}
            isMobile={props.isMobile}
        >
            {(filterItem: RadioType) => {
                return (
                    <FilterItem
                        ChoiceComponent={Checkbox}
                        key={constructFilterItemKey(
                            'language',
                            filterItem.value,
                            props.isMobile,
                        )}
                        value={filterItem.value}
                        display={filterItem.display}
                    />
                );
            }}
        </FilterSection>
    );
};

const CurrencyFilters: React.FunctionComponent<FilterProps> = (props) => {
    return (
        <FilterSection
            filters={{
                ...FORMATTED_CURRENCIES,
            }}
            onOptionChange={(value: any) => {
                props.onFilterChange?.(CURRENCY_FILTER, value);
            }}
            activeFilterOption={props.selectedFilters?.[CURRENCY_FILTER]}
            shouldScrollIntoView={props.filterTypeToShow === CURRENCY_FILTER}
            title={
                FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[CURRENCY_FILTER].label
            }
            isMobile={props.isMobile}
        >
            {(filterItem: RadioType) => {
                return (
                    <FilterItem
                        key={constructFilterItemKey(
                            'currency',
                            filterItem.value,
                            props.isMobile,
                        )}
                        value={filterItem.value}
                        display={filterItem.display}
                    />
                );
            }}
        </FilterSection>
    );
};

const Filters: React.FunctionComponent<FilterProps> = (props) => {
    return (
        <div>
            <CategoryFilters {...props} />
            <DateFilters {...props} />
            <ExperiencesFilters {...props} />
            <PriceFilters {...props} />
            <FormatFilters {...props} />
            <FollowedOrganizersFilterButton {...props} />
            <OnlineEventsFilterButton {...props} />
            <LanguageFilters {...props} />
            <CurrencyFilters {...props} />
        </div>
    );
};

export default Filters;
